/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { useEffect, useCallback, RefObject } from "react";

const REMOVAL_TIMEOUT = 150;

/**
 * There is a bug in Chrome where the backdrop-filter property is not applied correctly if the element is not in the DOM.
 * This hook forces a GPU re-render on the passed element to fix the bug.
 *
 * @param elementRef
 */
export const useBackdropFilterFix = (elementRef?: RefObject<HTMLElement | null>): void => {
	const applyBackdropFix = useCallback(() => {
		const targetElement = elementRef?.current;
		if (!targetElement) return;

		const originalTransform = targetElement.style.transform || "";
		const originalOpacity = targetElement.style.opacity || "";

		// Trigger reflow to fix Chrome backdrop-filter bug
		requestAnimationFrame(() => {
			if (targetElement?.parentNode && targetElement?.style && !originalTransform) {
				targetElement.style.transform = "translate3d(0, 0, 1px)"; //temporary force transform to force GPU re-render
				targetElement.style.opacity = "0.99"; //temporary adjust opacity to force GPU re-render
			}
		});

		// Waiting and then resetting is the most reliable way to fix the bug in testing
		setTimeout(() => {
			if (targetElement?.parentNode && targetElement?.style) {
				targetElement.style.transform = originalTransform;
				targetElement.style.opacity = originalOpacity;
			}
		}, REMOVAL_TIMEOUT);
	}, [elementRef]);

	//apply when its nearly fully visible for best results
	useEffect(() => {
		if (typeof window === "undefined" || !elementRef?.current) return;

		const targetElement = elementRef.current;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						//on quick scroll up, this timeout improves the reliability of the fix
						setTimeout(() => {
							applyBackdropFix();
						}, REMOVAL_TIMEOUT);
					}
				});
			},
			{
				threshold: 0.9,
			},
		);

		observer.observe(targetElement);

		return () => {
			observer.disconnect();
		};
	}, [elementRef, applyBackdropFix]);
};
